import React, { useContext, useState, useEffect } from 'react';
import {
    Box,
} from '@mui/material';

import { useSnackbar } from 'notistack';
import FormContext from '../contexts/FormContext';
import ProjectContext from '../contexts/ProjectContext';
import UserContext from '../contexts/UserContext';

import { useFetch } from '../components/auth/useFetch';
import IllusionViewMenuBar from '../components/menubar/IllusionViewMenuBar';
import IllusionOverviewDataGrid from '../components/datagrid/IllusionOverviewDataGrid';
import IllusionDetailDataGrid from '../components/datagrid/IllusionDetailDataGrid';
import ConfirmActionDialog from '../components/dialog/ConfirmActionDialog';

export default function IllusionView(props) {

    const { isLoading, setIsLoading, makeTour } = props 

    
    useEffect(() => {
        makeTour([
            {
                title: 'Illusion',
                target: '#illusion-overview-datagrid',
                content: 'This page gives a summary off all the VFX Pulls currently run through the system. The most recent pulls will appear at the top. Simply double click on a row to open up new tab with granular details of the pull. The tab will appear on the control bar next to the Illusion tab. As with all tables on Konsol, filtering and sorting can be applied to this page. The page will automatically check for updates every 10 seconds.',
                placementBeacon: 'left'
            },
            {
                title: 'Illusion',
                target: '#upload-illusion-edl',
                content: 'To submit a new VFX Pull request, click this button to upload a new EDL file. The EDL file will be processed and the VFX Pull will be added to the Illusion table.',
                placementBeacon: 'bottom',
                placement: 'bottom'
            },
            {
                title: 'Illusion',
                target: '#upload-illusion-resubmit',
                content: 'Plates can be resubmitted for re-rendering by selecting one or more rows using the checkboxes in the EDL detail tab. Once selected, click this button to resubmit the plates. The button is disabled on the Illusion overview tab.',
                placementBeacon: 'bottom',
                placement: 'bottom'
            },
            {
                title: 'Illusion',
                target: '#upload-illusion-ale',
                content: 'Once again, from the EDL granular details tab a ALE can be downloaded with all associated shots to the EDL as well as all the metadata. The button is disabled on the Illusion overview tab.',
                placement: 'bottom',
                placementBeacon: 'bottom',
                locale: {
                    next: 'Finish',
                }
            },
        ])
    }, [])

    const { setHasUnsavedChanges } = useContext(FormContext);
    const projectContext = useContext(ProjectContext);
    const { konsolUser } = useContext(UserContext)
    const fetchWithAuth = useFetch()
    const { enqueueSnackbar } = useSnackbar()

    const [edlName, setEdlName] = React.useState('--')
    const [isQTUploading, setIsQTUploading] = useState(false)
    const [isResubmitDialogOpen, setIsResubmitDialogOpen] = useState(false)
    const [isResubmitting, setIsResubmitting] = useState(false)
    const [qt, setQT] = useState(null)
    const [qtUploadProgress, setQTUploadProgress] = useState(0)
    const [selectedPlates, setSelectedPlates] = useState([])
    const [tabs, setTabs] = useState(['Illusion'])
    const [value, setValue] = useState(0);

    const handleSelectedPlatesChange = (plates) => {
        setSelectedPlates(plates);
    }

    useEffect(() => {
        if (value === 0) {
            setSelectedPlates([]);
        }
    }, [value]);

    const handleDetail = (edl_name) => {
        const newArray = [...tabs, edl_name];
        if (!tabs.includes(edl_name)){
            setTabs(newArray);
        }
        const index = newArray.indexOf(edl_name)
    };

    const resubmitEvent = async () => {
        if (selectedPlates.length === 0 || isResubmitting) return;

        setIsResubmitting(true);
        const user = `${konsolUser?.given_name ?? ''} ${konsolUser?.family_name ?? ''}`.trim();

        try {
            const responses = await Promise.all(
                selectedPlates.map((plate) =>
                    fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/resubmitevent/`, {
                        headers: {
                            'Content-Type': 'application/json',
                            'X-Project-ID': projectContext?.project_db
                        },
                        method: 'POST',
                        body: JSON.stringify({
                            edl_name: edlName,
                            plate,
                            user,
                        }),
                    })
                )
            );

            const failedCount = responses.filter((response) => !response.ok).length;
            if (failedCount > 0) {
                enqueueSnackbar(`Failed to resubmit ${failedCount} of ${selectedPlates.length} plate(s)`, { variant: 'error' });
            } else {
                enqueueSnackbar(
                    selectedPlates.length === 1
                        ? `Resubmitted ${selectedPlates[0]}`
                        : `Resubmitted ${selectedPlates.length} plates`,
                    { variant: 'success' }
                );
            }
        } catch (error) {
            console.error(error);
            enqueueSnackbar('Failed to resubmit plate(s)', { variant: 'error' });
        } finally {
            setIsResubmitting(false);
        }
    }

    const resubmitConfirmMessage = selectedPlates.length === 1
        ? `Are you sure you want to resubmit ${selectedPlates[0]} event?`
        : `Are you sure you want to resubmit ${selectedPlates.length} plates?`;

    const uploadQTFile = async () => {
        try {
            let response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/uploadfile/s3/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Project-ID': projectContext?.project_db
                },
                body: JSON.stringify({file_name: qt.name})
            })

            if(!response.ok){
                throw new Error('Could not fetch presigned URL')
            }

            setIsQTUploading(true)
            setHasUnsavedChanges(true)

            const { url, fields } = await response.json()
            const s3FormData = new FormData()
            Object.entries(fields).forEach(([key, value]) => {
                s3FormData.append(key, value);
            });
            s3FormData.append('file', qt);

            // Generated by GPT-4o
            // To get the progress of the upload, we have to use XMLHttpRequest instead of fetch
            const xhr = new XMLHttpRequest()

            xhr.upload.onprogress = (event) => {
                if(event.lengthComputable) {
                    const percentComplete = Math.round((event.loaded / event.total) * 100)
                    setQTUploadProgress(percentComplete)
                }
            }

            xhr.onload = () => {
                if ([200, 204].indexOf(xhr.status) === -1){
                    throw new Error(`Uploading ${qt.name} to S3 failed`);
                }
            }

            xhr.onerror = () => {
                throw new Error(`Uploading ${qt.name} to S3 failed`);
            }

            xhr.onloadend = () => {
                setIsQTUploading(false)
                setQT(null)
                setHasUnsavedChanges(false)
                setQTUploadProgress(0)
            }

            xhr.open('POST', url)
            xhr.send(s3FormData)

        }
        catch (err) {
            console.error(err)
            setIsQTUploading(false)
            setQT(null)
            setHasUnsavedChanges(false)
            setQTUploadProgress(0)
        }
    }


    return (
        <React.Fragment>
            <Box id='illusion-overview-datagrid'
                display="flex" 
                flexDirection="column" 
                height='100%'
                width='100%'
            >
                <IllusionViewMenuBar
                    edlName={edlName}
                    isQTUploading={isQTUploading}
                    qt={qt}
                    qtUploadProgress={qtUploadProgress}
                    selectedPlates={selectedPlates}
                    setEdlName={setEdlName}
                    setIsResubmitDialogOpen={setIsResubmitDialogOpen}
                    setQT={setQT}
                    setTabs={setTabs}
                    setValue={setValue}
                    tabs={tabs}
                    uploadQTFile={uploadQTFile}
                    value={value}
                />
                <Box
                    display='flex'
                    flexDirection='row'
                    bgcolor='rgba(1,1,1,0.7)'
                    sx={{
                        width: '100%',
                        height: 'calc(100% - 47px)',}}
                    overflow='auto'
                >  
                 { value === 0 && (
                    <IllusionOverviewDataGrid handleDetail={handleDetail} setLoading={setIsLoading} />
                )}
                 {tabs.map((tab, index) => (
                    value > 0 && value === index && (
                        value === index && <IllusionDetailDataGrid edl_name={tab} onSelectedPlatesChange={handleSelectedPlatesChange} setIsLoading={setIsLoading} />
                    )
                ))} 
                </Box>
            </Box>
            <ConfirmActionDialog
                clickAway={() => setIsResubmitDialogOpen(false)}
                handleClick={async () => {
                    await resubmitEvent()
                    setIsResubmitDialogOpen(false)
                }}
                open={isResubmitDialogOpen}
                message={resubmitConfirmMessage}
            />
        </React.Fragment>
    )
}
